<script setup>
import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import { useStore } from "vuex";
import {
  mdiForwardburger,
  mdiBackburger,
  mdiMenu,
  mdiMagnify,
  mdiChevronDown,
  mdiAccountCircle,
  mdiLogout,
  mdiBookOpenPageVariant,
  mdiWalletOutline,
  mdiCartOutline,
  mdiWeatherNight,
  mdiWhiteBalanceSunny,
  mdiHelpCircleOutline,
  mdiSchoolOutline,
} from "@mdi/js";
import NavBarItem from "@/components/NavBarItem.vue";
import Icon from "@/components/Icon.vue";
import NavBarSearch from "@/components/NavBarSearch.vue";
import LanguageSwitcher from "@/components/LanguageSwitcher.vue";
import NotificationBell from "@/components/alerts/NotificationBell.vue";
import UserAvatar from "@/components/UserAvatar.vue";
import { useRoute, useRouter } from "vue-router";
import AuthService from "../services/auth.service";
import { usePopover } from "@/composables/usePopover";
import { useTokenBalance } from "@/composables/useTokenBalance";
import { tutorialLocationFor } from "@/components/tutorial/tutorial-steps";

const store = useStore();
const router = useRouter();
const route = useRoute();

const isNavBarVisible = computed(() => !store.state.isFullScreen);

const isAsideMobileExpanded = computed(() => store.state.isAsideMobileExpanded);

const isAsideLgActive = computed(() => store.state.isAsideLgActive);

const isAsideCollapsed = computed(() => store.state.isAsideCollapsed);

const darkMode = computed(() => store.state.darkMode);

const userName = computed(() => store?.state?.auth?.user?.user?.name);

const userEmail = computed(() => store?.state?.auth?.user?.user?.email);

/* Token balance — one rule with the sidebar footer (useTokenBalance.js):
   neutral while funded, amber plus the words "Top up" when low. It used to be
   green/red here and grey/amber there, with the colour alone saying which. */
const {
  full: formattedTokens,
  compact: compactTokens,
  isLow: isLowBalance,
} = useTokenBalance();

const balanceLabel = computed(() =>
  isLowBalance.value
    ? `Token balance: ${formattedTokens.value} — top up`
    : `Token balance: ${formattedTokens.value} — buy tokens`
);

/* Cart. A line per site; the count is lines, which is also what checkout
   reports (`cart_items`). Only rendered while there is something in it. */
const cartCount = computed(() => store.state.cart?.items?.length ?? 0);

const cartLabel = computed(() =>
  `Cart, ${cartCount.value} ${cartCount.value === 1 ? "item" : "items"}`
);

/* The "?" button: the tutorial, opened at the step that explains the page you
   are on (tutorial-steps.js). Not shown on the tutorial itself. */
const tutorialTo = computed(() => tutorialLocationFor(route.path));
const onTutorial = computed(() => route.path === "/tutorial");

// Same cap as the notification bell's badge: three digits do not fit the dot.
const cartBadge = computed(() => (cartCount.value > 9 ? "9+" : String(cartCount.value)));

const menuToggleMobileIcon = computed(() =>
  isAsideMobileExpanded.value ? mdiBackburger : mdiForwardburger
);

const menuToggleMobile = () => store.dispatch("asideMobileToggle");

const menuOpenLg = () => {
  store.dispatch("asideLgToggle", true);
};

const toggleDarkMode = () => store.dispatch("darkMode");

const logout = () => {
  store.dispatch("auth/logout");
  router.push("/login");
};

/* Account dropdown */
const isUserMenuOpen = ref(false);
const userMenuRoot = ref(null);
const userMenuTrigger = ref(null);
const userMenuPanel = ref(null);

const toggleUserMenu = () => {
  isUserMenuOpen.value = !isUserMenuOpen.value;
};

const closeUserMenu = ({ restoreFocus = false } = {}) => {
  if (!isUserMenuOpen.value) return;
  isUserMenuOpen.value = false;
  if (restoreFocus) userMenuTrigger.value?.focus();
};

/* One header popover open at a time, focus into the menu on open, Tab past
   either end closes it — see composables/usePopover.js. */
const { panelId: userMenuId, onKeydown: onUserMenuKeydown } = usePopover({
  name: "account-menu",
  isOpen: isUserMenuOpen,
  close: closeUserMenu,
  trigger: userMenuTrigger,
  panel: userMenuPanel,
});

/* Mobile page search.
 *
 * NavBarSearch's own root is `hidden md:block`, so below 768px page search had
 * no entry point at all — its only other affordance is the ctrl+k shortcut,
 * which no phone can produce. That component belongs to another owner, so
 * rather than changing its breakpoint we mount a second instance here inside a
 * sheet that drops out of the navbar, and un-hide that instance from this
 * file's scoped CSS. Mounting it (instead of reimplementing the menu.js
 * filtering) keeps a single source of truth for what "search" means. */
const isMobileSearchOpen = ref(false);
const mobileSearchRoot = ref(null);
const mobileSearchTrigger = ref(null);

const closeMobileSearch = ({ restoreFocus = false } = {}) => {
  if (!isMobileSearchOpen.value) return;
  isMobileSearchOpen.value = false;
  if (restoreFocus) mobileSearchTrigger.value?.$el?.focus();
};

const toggleMobileSearch = () => {
  isMobileSearchOpen.value = !isMobileSearchOpen.value;
};

/* Opening search now closes whichever other header panel was open (two panels
   stacked on a 390px screen), through the same coordinator as the menus.
   NavBarSearch exposes no `focus()`, and adding one would mean editing it, so
   the initial focus queries the sheet for its input — without it the user has
   to tap twice to start typing. */
const { panelId: mobileSearchId, onKeydown: onMobileSearchKeydown } = usePopover({
  name: "search",
  isOpen: isMobileSearchOpen,
  close: closeMobileSearch,
  trigger: computed(() => mobileSearchTrigger.value?.$el ?? null),
  panel: mobileSearchRoot,
  initialFocus: () => mobileSearchRoot.value?.querySelector("input") ?? null,
});

const onDocumentClick = (event) => {
  if (userMenuRoot.value && !userMenuRoot.value.contains(event.target)) {
    closeUserMenu();
  }
  // The toggle itself stops propagation, so reaching here means the click
  // landed somewhere else on the page.
  if (
    isMobileSearchOpen.value &&
    mobileSearchRoot.value &&
    !mobileSearchRoot.value.contains(event.target)
  ) {
    closeMobileSearch();
  }
};

/* Escape hands focus back to the trigger, but only from inside the panel or
   its trigger: pressed anywhere else, the reader is not standing in the header
   and must not be yanked there. */
const onDocumentKeydown = (event) => {
  if (event.key !== "Escape") return;
  const active = document.activeElement;
  closeUserMenu({
    restoreFocus: !!(userMenuRoot.value && userMenuRoot.value.contains(active)),
  });
  closeMobileSearch({
    restoreFocus:
      !!(mobileSearchRoot.value && mobileSearchRoot.value.contains(active)) ||
      !!(mobileSearchTrigger.value?.$el && mobileSearchTrigger.value.$el.contains(active)),
  });
};

watch(() => route.fullPath, () => {
  closeUserMenu();
  // Picking a result navigates; the sheet must not stay open over the new page.
  closeMobileSearch();
});

// Updating user stats periodically
const timer = ref(undefined);

onMounted(() => {
  const user = JSON.parse(localStorage.getItem("user"));
  if (user.access_token && !user.user) {
    logout();
    return;
  }

  /* Background user refresh — runs on every page and again every 60s. A
   * rejection here must stay silent: a genuinely expired session is already
   * handled by the response interceptor (`auth/sessionExpired`), whereas an
   * unhandled rejection would surface the global "Something went wrong" toast
   * on whatever page the user happened to be looking at. */
  AuthService.getUser().catch(() => {});
  timer.value = setInterval(() => AuthService.getUser().catch(() => {}), 60000);
});

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
  document.addEventListener("keydown", onDocumentKeydown);
});

onUnmounted(() => {
  clearInterval(timer.value);
  document.removeEventListener("click", onDocumentClick);
  document.removeEventListener("keydown", onDocumentKeydown);
});
</script>

<template>
  <!-- `transition-all` is deliberate: see AsideMenu. This bar animates the
       inline padding/offset that tracks the sidebar collapsing and the mobile
       drawer pushing it across — neither is in Tailwind's curated `transition`
       set (colour, opacity, shadow, transform), so swapping it makes the bar
       jump instead of glide.

       `z-[32]`: one step above the toast stack (ToastHost, z-[31]) so the
       account menu and the search sheet, which hang below the bar INSIDE its
       stacking context, are never covered by a toast; one step below the
       drawer's backdrop (App.vue, z-[33]), which has always dimmed the bar.
       Page content tops out at z-30 (the campaigns table's sticky header). -->
  <nav
    v-show="isNavBarVisible"
    class="navbar-inline top-0 left-0 right-0 fixed flex items-center h-14 z-[32] w-full transition-all duration-300 lg:w-auto
           bg-white bg-opacity-80 backdrop-blur border-b border-gray-200
           dark:bg-gray-800 dark:bg-opacity-80 dark:border-gray-700"
    :style="{
      '--navbar-pad': isAsideCollapsed ? '4rem' : '15rem',
      '--navbar-shift': isAsideMobileExpanded ? '15rem' : '0px'
    }"
  >
    <div class="flex-1 flex items-center h-14 gap-1 px-2 sm:px-3">
      <!-- `min-w/min-h` rather than `w-11 h-11`: the floor only has to raise the
           hit area to the 44px touch minimum, leaving the icon's own box and the
           lg+ sizing untouched. -->
      <!-- The two hamburgers open the drawer (AsideMenu, #app-sidebar): below
           lg as `isAsideMobileExpanded`, between lg and xl as
           `isAsideLgActive`. `aria-expanded` reports the flag each one drives. -->
      <nav-bar-item
        type="flex lg:hidden"
        class="min-w-[2.75rem] min-h-[2.75rem] sm:min-w-0 sm:min-h-0"
        aria-label="Toggle navigation menu"
        aria-controls="app-sidebar"
        :aria-expanded="isAsideMobileExpanded ? 'true' : 'false'"
        @click.prevent="menuToggleMobile"
      >
        <icon :path="menuToggleMobileIcon" size="24" />
      </nav-bar-item>
      <nav-bar-item
        type="hidden lg:flex xl:hidden"
        aria-label="Open navigation menu"
        aria-controls="app-sidebar"
        :aria-expanded="isAsideLgActive ? 'true' : 'false'"
        @click.prevent="menuOpenLg"
      >
        <icon :path="mdiMenu" size="24" />
      </nav-bar-item>

      <!-- Mobile entry point to page search. Hidden from md up, where
           NavBarSearch's own always-visible input takes over. -->
      <nav-bar-item
        ref="mobileSearchTrigger"
        type="flex md:hidden"
        class="min-w-[2.75rem] min-h-[2.75rem] sm:min-w-0 sm:min-h-0"
        aria-label="Search pages"
        :aria-expanded="isMobileSearchOpen"
        :aria-controls="isMobileSearchOpen ? mobileSearchId : undefined"
        @click.stop="toggleMobileSearch"
      >
        <icon :path="mdiMagnify" size="24" />
      </nav-bar-item>

      <nav-bar-search />

      <div class="flex-1" />

      <!-- Width budget, so nothing is pushed off the right edge: below sm the
           language control moves into the account menu and the gaps tighten;
           below 360px the cart button does too (scoped CSS below); below lg
           the account button drops the name (it is in the menu); below xl the
           language button shows its code instead of its name.
           `min-w-0` is the backstop for what no budget can predict — a
           machine-translated "Top up" three times longer, say: this cluster
           may shrink, and the one thing in it that can give is the balance
           pill, whose words truncate. Every other control keeps its size, so
           the account menu (and Log out, which only it holds) never leaves
           the screen. -->
      <div class="flex min-w-0 items-center gap-1 sm:gap-2">
        <!-- Token balance. Neutral while funded; amber plus the words "Top up"
             when low, so colour is never the only signal. Shown at every width
             now — below sm it used to be hidden outright, on the screen where
             it is hardest to find anywhere else. Phones get the compact figure
             ("123K"), or, when low, the words alone; the exact figure is in
             the label either way. -->
        <router-link
          to="/products"
          class="inline-flex min-w-0 items-center gap-1.5 sm:gap-2 h-9 min-h-[2.75rem] sm:min-h-0 px-2 sm:px-3 rounded-xl border text-sm font-semibold whitespace-nowrap transition-colors
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2
                 dark:focus-visible:ring-offset-gray-800"
          :class="isLowBalance
            ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-500 dark:border-opacity-40 dark:bg-amber-500 dark:bg-opacity-10 dark:text-amber-300 dark:hover:bg-opacity-20'
            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'"
          :aria-label="balanceLabel"
          :title="balanceLabel"
        >
          <icon
            :path="mdiWalletOutline"
            :size="18"
            w="w-5"
            h="h-5"
            class="flex-none"
            :class="isLowBalance ? '' : 'text-gray-400 dark:text-gray-500'"
          />
          <span v-if="!isLowBalance" class="min-w-0 truncate tabular-nums sm:hidden">{{ compactTokens }}</span>
          <span class="tabular-nums hidden sm:inline">{{ formattedTokens }}</span>
          <span v-if="isLowBalance" class="min-w-0 truncate">Top up</span>
        </router-link>

        <!-- The cart, only while it holds something. Styled from the bell
             beside it, with a brand badge rather than a red one: a full cart
             is not an alert. Below 360px it moves into the account menu. -->
        <router-link
          v-if="cartCount > 0"
          to="/checkout"
          class="cart-in-bar relative inline-flex items-center justify-center w-9 h-9 min-w-[2.75rem] min-h-[2.75rem] sm:min-w-0 sm:min-h-0 rounded-xl border transition-colors
                 border-gray-200 bg-white text-gray-500 hover:text-brand-600 hover:border-brand-300 hover:bg-brand-50
                 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-brand-300 dark:hover:bg-gray-700
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2
                 dark:focus-visible:ring-offset-gray-800"
          :aria-label="cartLabel"
          :title="cartLabel"
        >
          <icon :path="mdiCartOutline" :size="18" w="w-5" h="h-5" />
          <span
            class="absolute -top-1 -right-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand-600 px-1 text-3xs font-semibold leading-none text-white ring-2 ring-white dark:ring-gray-800"
            aria-hidden="true"
          >
            {{ cartBadge }}
          </span>
        </router-link>

        <!-- How does this page work? Opens the tutorial at this page's step.
             Styled like the cart and the bell beside it. From xl: measured
             at 1280 with the docked sidebar, the longest language name and a
             full cart, there is room to spare (shell.spec.mjs keeps it so).
             Narrower screens get the same link from the sidebar footer's
             "Guide" and the account menu's "Tutorial". -->
        <router-link
          v-if="!onTutorial"
          :to="tutorialTo"
          class="hidden xl:inline-flex relative items-center justify-center w-9 h-9 rounded-xl border transition-colors
                 border-gray-200 bg-white text-gray-500 hover:text-brand-600 hover:border-brand-300 hover:bg-brand-50
                 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-brand-300 dark:hover:bg-gray-700
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2
                 dark:focus-visible:ring-offset-gray-800"
          aria-label="How does this page work? Open the tutorial"
          title="How does this page work?"
          data-testid="navbar-tutorial"
        >
          <icon :path="mdiHelpCircleOutline" :size="18" w="w-5" h="h-5" />
        </router-link>

        <!-- Below sm this lives in the account menu instead (see there). -->
        <language-switcher compact class="hidden sm:block" />

        <!-- Notices support staff wrote about this account. Sized and styled
             from the dark-mode toggle below it so the header cluster stays one
             row of matching controls. Renders nothing at all while the backend
             cannot store alerts. -->
        <notification-bell />


        <!-- Account menu -->
        <div ref="userMenuRoot" class="relative">
          <!-- Name from lg, chevron from sm: at 768–860px the name, the search
               box and a long language name together pushed this button off the
               right edge, and at 375px every pixel goes to the balance. The
               name is at the top of the menu either way. `min-w` keeps the
               avatar-only phone button at the 44px touch minimum. -->
          <button
            ref="userMenuTrigger"
            type="button"
            class="inline-flex items-center justify-center gap-2 h-9 min-w-[2.75rem] min-h-[2.75rem] sm:min-w-0 sm:min-h-0 pl-1 pr-1 sm:pr-2 rounded-xl border border-transparent transition-colors
                   hover:bg-gray-100 dark:hover:bg-gray-700
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2
                   dark:focus-visible:ring-offset-gray-800"
            :class="isUserMenuOpen ? 'bg-gray-100 dark:bg-gray-700' : ''"
            aria-haspopup="true"
            :aria-expanded="isUserMenuOpen"
            :aria-controls="isUserMenuOpen ? userMenuId : undefined"
            aria-label="Open account menu"
            @click="toggleUserMenu"
          >
            <user-avatar :username="userName" class="w-7 h-7 flex-none" />
            <span class="hidden lg:block max-w-[10rem] truncate text-sm font-semibold text-gray-700 dark:text-gray-200">
              {{ userName || 'Account' }}
            </span>
            <icon
              :path="mdiChevronDown"
              :size="16"
              w="w-4"
              h="h-4"
              class="hidden sm:inline-flex flex-none text-gray-400 transition-transform duration-200"
              :class="isUserMenuOpen ? 'rotate-180' : ''"
            />
          </button>

          <transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="opacity-0 -translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 -translate-y-1"
          >
            <div
              v-if="isUserMenuOpen"
              :id="userMenuId"
              ref="userMenuPanel"
              class="anchor-end absolute mt-2 w-64 rounded-xl border shadow-card overflow-hidden z-40
                     bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700"
              @keydown="onUserMenuKeydown"
            >
              <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <user-avatar :username="userName" class="w-9 h-9 flex-none" />
                <div class="min-w-0">
                  <div class="text-sm font-semibold truncate text-gray-900 dark:text-gray-100">
                    {{ userName || 'User' }}
                  </div>
                  <div
                    class="text-xs truncate text-gray-500 dark:text-gray-400"
                    :title="userEmail"
                  >
                    {{ userEmail || '' }}
                  </div>
                </div>
              </div>

              <div class="py-1">
                <router-link
                  to="/account"
                  class="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors
                         text-gray-700 hover:bg-gray-50 hover:text-brand-600
                         dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-brand-400
                         focus:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-gray-700"
                  @click="closeUserMenu()"
                >
                  <icon :path="mdiAccountCircle" :size="18" w="w-5" h="h-5" class="flex-none" />
                  <span>Account</span>
                </router-link>
                <router-link
                  :to="tutorialTo"
                  class="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors
                         text-gray-700 hover:bg-gray-50 hover:text-brand-600
                         dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-brand-400
                         focus:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-gray-700"
                  @click="closeUserMenu()"
                >
                  <icon :path="mdiSchoolOutline" :size="18" w="w-5" h="h-5" class="flex-none" />
                  <span>Tutorial</span>
                </router-link>
                <!-- Below 360px only (scoped CSS): the bar's cart button moves
                     here, so a 320px screen with a full cart still fits. -->
                <router-link
                  v-if="cartCount > 0"
                  to="/checkout"
                  class="cart-in-menu items-center gap-3 px-4 py-2.5 text-sm transition-colors
                         text-gray-700 hover:bg-gray-50 hover:text-brand-600
                         dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-brand-400
                         focus:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-gray-700"
                  :aria-label="cartLabel"
                  @click="closeUserMenu()"
                >
                  <icon :path="mdiCartOutline" :size="18" w="w-5" h="h-5" class="flex-none" />
                  <span class="flex-1">Cart</span>
                  <span
                    class="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-brand-600 px-1.5 text-2xs font-semibold text-white"
                    aria-hidden="true"
                  >
                    {{ cartBadge }}
                  </span>
                </router-link>
                <!-- Moved out of the top bar. The bar is on every page, so a
                     control there is paid for on every page view; theme is set
                     once and then left alone for months. The sidebar footer
                     keeps its own one-click "Dark" button, so this is the
                     second way in, not the only one. -->
                <button
                  type="button"
                  class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors
                         text-gray-700 hover:bg-gray-50 hover:text-brand-600
                         dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-brand-400
                         focus:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-gray-700"
                  @click="toggleDarkMode"
                >
                  <icon :path="darkMode ? mdiWhiteBalanceSunny : mdiWeatherNight" :size="18" w="w-5" h="h-5" class="flex-none" />
                  <span>{{ darkMode ? 'Light mode' : 'Dark mode' }}</span>
                </button>
                <a
                  href="https://toplistbot.com/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-3 px-4 py-2.5 text-sm transition-colors
                         text-gray-700 hover:bg-gray-50 hover:text-brand-600
                         dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-brand-400
                         focus:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-gray-700"
                  @click="closeUserMenu()"
                >
                  <icon :path="mdiBookOpenPageVariant" :size="18" w="w-5" h="h-5" class="flex-none" />
                  <span>Documentation</span>
                </a>
                <!-- Phones only: the top bar has no room for the language
                     button next to the balance, the cart, the bell and this
                     menu, so below sm it lives here — as a native select,
                     which is the picker a phone already knows how to show. -->
                <language-switcher variant="select" class="sm:hidden" />
              </div>

              <div class="py-1 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  class="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors
                         text-gray-700 hover:bg-red-50 hover:text-red-600
                         dark:text-gray-300 dark:hover:bg-red-500 dark:hover:bg-opacity-10 dark:hover:text-red-400
                         focus:outline-none focus-visible:bg-red-50 dark:focus-visible:bg-red-500 dark:focus-visible:bg-opacity-10"
                  @click="logout"
                >
                  <icon :path="mdiLogout" :size="18" w="w-5" h="h-5" class="flex-none" />
                  <span>Log out</span>
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- Mobile search sheet. Drops out of the bottom edge of the bar rather
         than replacing the bar's contents, so the hamburger stays reachable
         while searching. `top-full` measures off the navbar itself (which is
         `fixed`, and therefore the containing block), so no height is
         duplicated here. -->
    <div
      v-if="isMobileSearchOpen"
      :id="mobileSearchId"
      ref="mobileSearchRoot"
      class="mobile-search md:hidden absolute inset-x-0 top-full z-40 px-2 pt-2 pb-3 border-b shadow-card
             bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700"
      @keydown="onMobileSearchKeydown"
    >
      <nav-bar-search />
    </div>
  </nav>
</template>

<style scoped>
/* `.navbar-inline` (css/_translate-rtl.css) offsets the bar by
   `margin-inline-start: var(--navbar-shift)` while the mobile drawer is open,
   but the bar is also `w-full` — so on a 390px screen the whole right-hand
   cluster was pushed past the right edge and the bar looked empty. Subtracting
   the same variable keeps the bar inside the viewport.

   Deliberately reads the variable instead of hardcoding 15rem: the drawer's
   width is owned elsewhere and is being changed, and if `--navbar-shift` ever
   stops being set this falls back to `0px`, i.e. exactly the `w-full` we have
   today. */
.navbar-inline {
  width: calc(100% - var(--navbar-shift, 0px));
}

/* From lg up the sidebar is docked and `.navbar-inline` zeroes the shift; the
   bar sizes itself off the `left-0 right-0` pair, which is what `lg:w-auto`
   does today. Restated here because the rule above outranks that utility. */
@media (min-width: 1024px) {
  .navbar-inline {
    width: auto;
  }
}

/* NavBarSearch's root carries `hidden md:block`, which is precisely what put
   page search out of reach below 768px. That component is owned elsewhere, so
   the sheet un-hides its root from here. Vue stamps this component's scope id
   onto a child component's root node, which is why `> *` reaches it without
   `:deep()` — and the extra attribute selector is what outranks `.hidden`.
   Scoped to `.mobile-search` so the md+ instance is untouched. */
.mobile-search > * {
  display: block;
  width: 100%;
}

/* The input is `w-56 lg:w-72`; a fixed 224px in a full-bleed sheet reads as a
   mistake. Its own scope id is NavBarSearch's, so this one needs `:deep()`. */
.mobile-search :deep(input) {
  width: 100%;
}

/* Below 360px the bar holds the hamburger, search, the balance, the bell and
   the account button with nothing to spare — a cart button on top pushed the
   account button (and Log out, which only its menu holds) off a 320px screen.
   So there the cart moves into the account menu. Plain CSS because Tailwind
   2.2 has no breakpoint under `sm` and no arbitrary variants. The scoped
   attribute is what outranks the `inline-flex` utility. */
.cart-in-menu {
  display: none;
}

@media (max-width: 359.98px) {
  .cart-in-bar {
    display: none;
  }

  .cart-in-menu {
    display: flex;
  }
}
</style>
